import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { mountShell } from '../components/Shell.js';
import { flashToast, showToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import {
  bindVerifyPromptButtons,
  isEmailNotVerifiedError,
  verifyEmailPromptHtml,
} from '../components/VerifyEmailPrompt.js';
import { titleChipHtml } from '../components/Title.js';
import { openAttackBanner } from '../components/AttackBanner.js';
import type {
  AttackPlayerResponse,
  CombatRoundDto,
  DarkRaidStatusResponse,
  LoadoutDto,
  PlayersHereResponse,
  PvpOutcomeChoice,
} from '@rex-mortis/shared';

// Single live handle for the Shade countdown ticker. renderMain re-runs on every
// re-render (confront, search, refresh); without clearing the prior interval they
// accumulate and each writes its own (now stale) iso to the same #shade-cd
// element — a just-marked 12h window (~11h) fighting the fresh 24h wound, which
// is the 11h↔24h flicker. Keep exactly one ticker alive.
let shadeCdTimer: number | null = null;

/** The Shade banner — shown when the player is marked or wounded by the dark
 *  NPC raid. Marked players get a Confront button; wounded players see the
 *  lingering penalty and when it heals. */
function shadeBannerHtml(dr: DarkRaidStatusResponse | null): string {
  if (!dr) return '';
  if (dr.marked) {
    return `<div class="shade-banner marked">
      <img class="shade-banner-art" src="/img/shade/shade_banner.png" alt="" onerror="this.style.display='none'" />
      🕯 <strong>A Shade has marked you.</strong> It hunts the realm's mightiest. Confront it
      now — win and you are safe; lose or ignore it and it cuts you down: every battle stat
      wounded, and you are dragged broken into the Infirmary.
      <span class="shade-cd">Mark turns to a wound in <span id="shade-cd">${shadeRemaining(dr.windowEndsAt)}</span>.</span>
      <div style="margin-top: var(--s-2);"><button class="btn" id="confront-shade">Confront the Shade</button></div>
    </div>`;
  }
  if (dr.wound) {
    return `<div class="shade-banner wounded">
      🩸 <strong>You bear a Shade-wound.</strong> Every battle stat is cut by ${dr.wound.pct}% in combat.
      <span class="shade-cd">Heals in <span id="shade-cd">${shadeRemaining(dr.wound.healsAt)}</span>.</span>
    </div>`;
  }
  return '';
}

function shadeRemaining(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'now';
  const sec = Math.ceil(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const SHADE_CSS = `
  .shade-banner {
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
    margin-bottom: var(--s-3);
    font-size: 0.92rem; line-height: 1.45;
  }
  .shade-banner.marked {
    background: rgba(40, 10, 60, 0.14);
    border: 1px solid #5b2a7a; border-left: 4px solid #7d3aa6;
  }
  .shade-banner.wounded {
    background: rgba(120, 20, 20, 0.12);
    border: 1px solid var(--c-oxblood); border-left: 4px solid var(--c-oxblood);
  }
  .shade-cd { display: block; margin-top: 2px; font-family: var(--font-numeric); color: var(--c-ink-soft); }
  .shade-banner-art {
    width: 100%; display: block; border-radius: var(--radius);
    margin-bottom: var(--s-2); border: 1px solid #5b2a7a;
  }
`;

/** Caution banner shown before a fight when the player is missing gear — the
 *  no-armour case in particular catches new players who take full damage. */
function gearWarningHtml(loadout: LoadoutDto | null): string {
  if (!loadout) return '';
  const noArmour = loadout.totalArmor <= 0;
  const noWeapon = loadout.totalWeaponAttack <= 0;
  if (!noArmour && !noWeapon) return '';
  const parts: string[] = [];
  if (noArmour) parts.push('no armour equipped — you take <strong>full damage</strong>');
  if (noWeapon) parts.push('no weapon equipped — you fight <strong>bare-fisted</strong>');
  return `<div class="gear-warning">⚔ You have ${parts.join(' and ')}. Equip a weapon, helm, chest, and shield from your <a href="#/inventory">Inventory</a> before you fight.</div>`;
}

const GEAR_WARNING_CSS = `
  .gear-warning {
    background: rgba(212, 169, 60, 0.14);
    border: 1px solid var(--c-gold);
    border-left: 4px solid var(--c-gold);
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
    margin-bottom: var(--s-3);
    color: var(--c-ink);
    font-size: 0.92rem;
    line-height: 1.4;
  }
  .gear-warning a { color: var(--c-oxblood); font-weight: 700; }
`;

interface FlashState {
  kind: 'success' | 'danger' | 'warn' | 'verify';
  message: string;
}

interface Foundling {
  id: string;
  name: string;
  level: number;
  location: string;
  status: string;
}

export async function renderAttacks(root: HTMLElement): Promise<void> {
  let target: Foundling | null = null;
  let lastFight: AttackPlayerResponse | null = null;
  let flash: FlashState | null = null;
  let searchValue = '';
  let autoSearched = false;
  // Deep-link support: /attacks?target=Name (e.g. the Attack button on a profile).
  const initialTarget = (() => {
    const h = window.location.hash;
    const q = h.indexOf('?');
    if (q < 0) return '';
    return new URLSearchParams(h.slice(q + 1)).get('target') ?? '';
  })();
  if (initialTarget) searchValue = initialTarget;

  let loadout: LoadoutDto | null = null;
  let darkRaid: DarkRaidStatusResponse | null = null;
  let playersHere: PlayersHereResponse | null = null;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
  } catch {
    // tolerated
  }
  try {
    loadout = (await api.getLoadout()).loadout;
  } catch {
    // tolerated — the warning just won't show
  }
  try {
    // Polling this also resolves any pending Shade mark/lapse/heal server-side.
    darkRaid = await api.darkRaidStatus();
  } catch {
    // tolerated — the Shade banner just won't show
  }
  try {
    playersHere = await api.playersHere();
  } catch {
    // tolerated — the "who's here" list just won't show
  }

  mountShell(root, (main) => {
    // After a strike from the pop-out: keep the detailed replay panel updated,
    // clear any stale flash, refresh the roster (the foe is now infirmary'd),
    // and re-render. The pop-out already floats the result toast.
    const onAttackResolved = async (r: AttackPlayerResponse): Promise<void> => {
      lastFight = r;
      flash = null;
      try {
        playersHere = await api.playersHere();
      } catch {
        /* keep the stale roster rather than blow up the result */
      }
      anchored(renderMain);
    };

    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      // "Who's here" only makes sense once you've actually ARRIVED. While
      // travelling your location column is still your origin, so the roster
      // would be stale/misleading — hide it in transit (both legs).
      const onRoad = char.status === 'TRAVELLING';
      const herePanel = onRoad
        ? ''
        : `
        <section class="panel">
          <h2 style="margin-top:0;">Who's here${playersHere ? ` — ${escapeHtml(playersHere.locationName)}` : ''}</h2>
          <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.9rem;">
            Every soul currently in your location.
          </p>
          <div id="here-list" class="here-list"></div>
        </section>`;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/attacks.webp" alt="" />
        <h1>Attacks</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Search by name. Pick your aftermath. Live with the renown.
        </p>

        <div id="flash"></div>
        ${shadeBannerHtml(darkRaid)}
        ${gearWarningHtml(loadout)}

        <section class="panel">
          <h2 style="margin-top:0;">Find a target</h2>
          <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.9rem;">
            They must be in the same kingdom as you, and new arrivals are protected for their first 3 days (unless you're also new). Any level may attack any level. 25 vigor per attack.
          </p>
          <div style="display:flex; gap: var(--s-2); align-items: end; flex-wrap: wrap;">
            <div class="field" style="flex: 1; min-width: 200px; margin-bottom: 0; position: relative;">
              <label for="search">Character name</label>
              <input id="search" type="text" autocomplete="off" placeholder="start typing a name…" value="${escapeHtml(searchValue)}" />
              <div id="suggest" class="suggest-box" style="display:none;"></div>
            </div>
            <button class="btn" id="search-btn">Search</button>
          </div>
          <div id="target-panel" style="margin-top: var(--s-3);"></div>
        </section>

        ${herePanel}

        <section id="fight-replay"></section>
      `;

      const style = document.createElement('style');
      style.textContent = `
        ${GEAR_WARNING_CSS}
        ${SHADE_CSS}
        .here-list { display: block; }
        .here-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--s-3); padding: var(--s-2) var(--s-3); margin-bottom: var(--s-2);
          width: 100%; box-sizing: border-box;
          background: var(--c-parchment); border: 1px solid var(--c-rule-soft);
          border-radius: var(--radius);
        }
        .here-row:last-child { margin-bottom: 0; }
        .here-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1 1 auto; }
        .here-name { font-family: var(--font-display); font-weight: 600; color: var(--c-ink); }
        .here-meta { font-family: var(--font-numeric); font-size: 0.82rem; color: var(--c-ink-soft); }
        .here-row .btn { flex: 0 0 auto; padding: 6px 14px; font-size: 0.9rem; }
        .here-empty { color: var(--c-ink-soft); font-style: italic; }
        .suggest-box {
          position: absolute; top: 100%; left: 0; right: 0; z-index: 40;
          margin-top: 4px; max-height: 280px; overflow-y: auto;
          background: var(--c-parchment); border: 1px solid var(--c-rule-strong);
          border-radius: var(--radius); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }
        .suggest-item {
          display: flex; align-items: baseline; justify-content: space-between; gap: var(--s-2);
          padding: 7px 10px; cursor: pointer; border-bottom: 1px solid var(--c-rule-soft);
        }
        .suggest-item:last-child { border-bottom: none; }
        .suggest-item:hover, .suggest-item.active { background: rgba(180, 140, 40, 0.16); }
        .suggest-name { font-family: var(--font-display); color: var(--c-ink); }
        .suggest-meta { font-family: var(--font-numeric); font-size: 0.8rem; color: var(--c-ink-soft); }
        .target-card {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
        }
        .target-card .name { font-family: var(--font-display); font-size: 1.1rem; }
        .target-card .meta {
          display: flex; flex-wrap: wrap; gap: var(--s-2);
          margin-top: 4px; font-size: 0.85rem;
        }
        .target-card .pill {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
        }
        .target-card .pill.warn {
          border-color: var(--c-danger);
          color: var(--c-danger);
        }
        .target-card .actions {
          display: flex; gap: var(--s-2); margin-top: var(--s-3); flex-wrap: wrap;
        }
        .target-card .btn { padding: 6px 14px; font-size: 0.9rem; }
        .replay {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-4);
          margin-top: var(--s-4);
        }
        .replay h2 { margin-top: 0; }
        .replay.WIN { border-color: var(--c-success); }
        .replay.LOSS { border-color: var(--c-danger); }
        .replay.DRAW { border-color: var(--c-rule); }
        .replay .round {
          font-family: var(--font-numeric);
          font-size: 0.9rem;
          padding: 4px 8px;
          border-bottom: 1px solid var(--c-rule-soft);
        }
        .replay .round:last-child { border-bottom: 0; }
        .replay .renown-line { color: var(--c-gold-text); }
        .replay .renown-breakdown { color: var(--c-ink-soft); font-size: 0.92em; }
      `;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        if (flash.kind === 'verify') {
          // Verify prompt stays inline — it has interactive buttons.
          flashRoot.innerHTML = verifyEmailPromptHtml(flash.message);
          bindVerifyPromptButtons(flashRoot);
        } else {
          // Result/error → floating toast (overlays in place; no scroll-to-top).
          flashToast(flash);
        }
      }

      // Confront the Shade.
      const confrontBtn = main.querySelector<HTMLButtonElement>('#confront-shade');
      confrontBtn?.addEventListener('click', async () => {
        confrontBtn.disabled = true;
        try {
          const r = await api.darkRaidConfront();
          store.set({ character: r.character });
          if (r.outcome === 'WIN') {
            flash = {
              kind: 'success',
              message: `You drove the Shade back into the dark. +${r.xpGained} XP, +${formatCrowns(r.crownsGained)} crowns.`,
            };
          } else {
            flash = {
              kind: 'warn',
              message: `The Shade bested you and cut you down — every battle stat wounded by ${r.wound?.pct ?? 0}%, and you are left broken in the Infirmary.`,
            };
          }
          darkRaid = await api.darkRaidStatus();
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'The Shade slips away.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Live Shade countdown. Clear any prior ticker first so re-renders never
      // stack intervals writing conflicting times to #shade-cd.
      if (shadeCdTimer !== null) { window.clearInterval(shadeCdTimer); shadeCdTimer = null; }
      if (darkRaid && (darkRaid.marked || darkRaid.wound)) {
        const iso = darkRaid.marked ? darkRaid.windowEndsAt : darkRaid.wound?.healsAt ?? null;
        const cdEl = main.querySelector<HTMLSpanElement>('#shade-cd');
        if (cdEl && iso) {
          shadeCdTimer = window.setInterval(() => {
            const el = main.querySelector<HTMLSpanElement>('#shade-cd');
            if (!el) {
              if (shadeCdTimer !== null) window.clearInterval(shadeCdTimer);
              shadeCdTimer = null;
              return;
            }
            el.textContent = shadeRemaining(iso);
          }, 1000);
        }
      }

      // Search wiring.
      const searchInput = main.querySelector<HTMLInputElement>('#search')!;
      const searchBtn = main.querySelector<HTMLButtonElement>('#search-btn')!;

      const doSearch = async () => {
        const name = searchInput.value.trim();
        searchValue = name;
        if (!name) {
          target = null;
          flash = null;
          anchored(renderMain);
          return;
        }
        searchBtn.disabled = true;
        try {
          const r = await api.findTarget({ name });
          if (r.found && r.target) {
            target = r.target;
            flash = null;
          } else {
            target = null;
            flash = { kind: 'warn', message: `No character "${name}" — or you can't attack them.` };
          }
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Search failed.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      };
      searchBtn.addEventListener('click', doSearch);

      // ── Typeahead suggestions ───────────────────────────────────────────────
      const suggestBox = main.querySelector<HTMLDivElement>('#suggest');
      let suggestTimer = 0;
      let activeIdx = -1; // keyboard-highlighted row

      const hideSuggest = (): void => {
        if (!suggestBox) return;
        suggestBox.style.display = 'none';
        suggestBox.innerHTML = '';
        activeIdx = -1;
      };
      const choose = (name: string): void => {
        searchInput.value = name;
        hideSuggest();
        void doSearch();
      };
      const renderSuggest = (items: { name: string; level: number; location: string; status: string }[]): void => {
        if (!suggestBox) return;
        if (items.length === 0) {
          hideSuggest();
          return;
        }
        activeIdx = -1;
        suggestBox.innerHTML = items
          .map(
            (s) => `
            <div class="suggest-item" data-name="${escapeHtml(s.name)}">
              <span class="suggest-name">${escapeHtml(s.name)}</span>
              <span class="suggest-meta">Lvl ${s.level} · ${escapeHtml(s.location)} · ${escapeHtml(s.status)}</span>
            </div>`,
          )
          .join('');
        suggestBox.style.display = 'block';
        suggestBox.querySelectorAll<HTMLDivElement>('.suggest-item').forEach((el) => {
          // mousedown (not click) so it fires before the input's blur hides us.
          el.addEventListener('mousedown', (e) => {
            e.preventDefault();
            choose(el.dataset.name!);
          });
        });
      };

      searchInput.addEventListener('input', () => {
        const q = searchInput.value.trim();
        window.clearTimeout(suggestTimer);
        if (q.length < 1) {
          hideSuggest();
          return;
        }
        suggestTimer = window.setTimeout(async () => {
          try {
            const r = await api.suggestTargets(q);
            if (searchInput.value.trim() === q) renderSuggest(r.suggestions); // ignore stale
          } catch {
            /* suggestions are best-effort */
          }
        }, 160);
      });

      searchInput.addEventListener('keydown', (e) => {
        const rows = suggestBox && suggestBox.style.display !== 'none'
          ? Array.from(suggestBox.querySelectorAll<HTMLDivElement>('.suggest-item'))
          : [];
        if (rows.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          activeIdx =
            e.key === 'ArrowDown'
              ? (activeIdx + 1) % rows.length
              : (activeIdx - 1 + rows.length) % rows.length;
          rows.forEach((r, i) => r.classList.toggle('active', i === activeIdx));
          return;
        }
        if (e.key === 'Enter') {
          const row = activeIdx >= 0 ? rows[activeIdx] : undefined;
          if (row) {
            e.preventDefault();
            choose(row.dataset.name!);
          } else {
            void doSearch();
          }
          return;
        }
        if (e.key === 'Escape') hideSuggest();
      });

      searchInput.addEventListener('blur', () => {
        window.setTimeout(hideSuggest, 120);
      });

      // Auto-run the search once when arriving via a profile deep-link.
      if (initialTarget && !autoSearched) {
        autoSearched = true;
        void doSearch();
      }

      // "Who's here" roster — pick a co-located target straight off the list.
      // The panel is absent while travelling, so guard the lookup.
      const hereList = main.querySelector<HTMLDivElement>('#here-list');
      if (!hereList) {
        // not present (in transit) — nothing to wire
      } else if (!playersHere || playersHere.players.length === 0) {
        hereList.innerHTML = `<p class="here-empty">No-one else is here right now.</p>`;
      } else {
        hereList.className = 'here-list';
        hereList.innerHTML = playersHere.players
          .map((p) => {
            const statusLabel = p.status === 'OKAY' ? 'Open to attack' : statusPhrase(p.status);
            return `
              <div class="here-row">
                <div class="here-id">
                  <span class="here-name"><a href="#/u/${encodeURIComponent(p.id)}">${escapeHtml(p.name)}</a>${titleChipHtml(p.activeTitleSlug)}</span>
                  <span class="here-meta">Level ${p.level} · ${escapeHtml(statusLabel)}</span>
                </div>
                <button class="btn ${p.attackable ? '' : 'ghost'}" data-here="${escapeHtml(p.name)}" title="${escapeHtml(p.reason)}" ${p.attackable ? '' : 'disabled'}>
                  ${p.attackable ? 'Attack' : escapeHtml(p.reason)}
                </button>
              </div>`;
          })
          .join('');
        hereList.querySelectorAll<HTMLButtonElement>('button[data-here]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            const name = btn.dataset.here!;
            const player = playersHere?.players.find((x) => x.name === name);
            if (!player) return;
            // Strike straight from the roster via the floating pop-out — no need
            // to drop the name into the search box first. Players here are all at
            // the viewer's own location.
            openAttackBanner(
              { id: player.id, name: player.name, level: player.level, status: player.status, location: char.location },
              { onResult: onAttackResolved },
            );
          });
        });
      }

      // Target panel.
      const tp = main.querySelector<HTMLDivElement>('#target-panel')!;
      if (target) {
        const sameLocation = target.location === char.location;
        const cantAttack =
          target.status !== 'OKAY' ||
          !sameLocation ||
          char.vigor < 25 ||
          char.life <= 0 ||
          char.status !== 'OKAY';
        let reason = '';
        if (target.status !== 'OKAY') reason = `Target is ${statusPhrase(target.status)}.`;
        else if (!sameLocation) reason = `You're in ${char.location}, target is in ${target.location}.`;
        else if (char.status !== 'OKAY') reason = `You are ${statusPhrase(char.status)}.`;
        else if (char.life <= 0) reason = 'You have no life left.';
        else if (char.vigor < 25) reason = `Need 25 vigor (have ${char.vigor}).`;

        tp.innerHTML = `
          <div class="target-card">
            <div class="name"><a href="#/u/${encodeURIComponent(target.id)}">${escapeHtml(target.name)}</a></div>
            <div class="meta">
              <span class="pill">level ${target.level}</span>
              <span class="pill">${escapeHtml(target.location)}</span>
              <span class="pill ${target.status === 'OKAY' ? '' : 'warn'}">${target.status}</span>
            </div>
            ${cantAttack ? `<p style="margin: var(--s-2) 0 0; color: var(--c-danger);">${escapeHtml(reason)}</p>` : ''}
            <div class="actions">
              <button class="btn" id="open-attack" ${cantAttack ? 'disabled' : ''}>Attack ${escapeHtml(target.name)}</button>
            </div>
          </div>
        `;
        tp.querySelector<HTMLButtonElement>('#open-attack')?.addEventListener('click', () => {
          openAttackBanner(
            {
              id: target!.id,
              name: target!.name,
              level: target!.level,
              location: target!.location,
              status: target!.status,
            },
            { onResult: onAttackResolved },
          );
        });
      } else {
        tp.innerHTML = '';
      }

      // Replay.
      const fr = main.querySelector<HTMLDivElement>('#fight-replay')!;
      if (lastFight) {
        const head =
          lastFight.outcome === 'WIN'
            ? `Victory over ${escapeHtml(lastFight.defenderName)} (${lastFight.choice})`
            : lastFight.outcome === 'LOSS'
              ? `Defeated by ${escapeHtml(lastFight.defenderName)}`
              : `Drew against ${escapeHtml(lastFight.defenderName)}`;
        fr.innerHTML = `
          <div class="replay ${lastFight.outcome}">
            <h2>${head}</h2>
            <p>${lastFight.damageDealt} dealt, ${lastFight.damageReceived} taken. ${lastFight.rounds.length} rounds.
              ${lastFight.xpGained > 0 ? `+${formatBigNum(lastFight.xpGained)} XP.` : ''}
              ${lastFight.crownsPlundered !== '0' ? `+${formatCrowns(lastFight.crownsPlundered)} crowns plundered.` : ''}
              ${lastFight.renown && lastFight.renown.total > 0 ? `<br><strong class="renown-line">⚜ House Renown +${lastFight.renown.total}</strong> <span class="renown-breakdown">(${renownBreakdownText(lastFight.renown)}).</span>` : ''}
              ${lastFight.levelsGained > 0 ? `🏆 +${lastFight.levelsGained} level${lastFight.levelsGained > 1 ? 's' : ''}!` : ''}
              ${lastFight.attackerHospitalUntil ? `<br><strong>Hospitalised until ${new Date(lastFight.attackerHospitalUntil).toLocaleTimeString()}.</strong>` : ''}
              ${lastFight.defenderHospitalUntil ? `<br>Defender hospitalised until ${new Date(lastFight.defenderHospitalUntil).toLocaleTimeString()}.` : ''}
            </p>
            <div>${lastFight.rounds.map((r) => roundHtml(r)).join('')}</div>
          </div>
        `;
      }
    };
    anchored(renderMain);
  });
}

function roundHtml(r: CombatRoundDto): string {
  const a = r.attacker.hit ? `you hit for ${r.attacker.damage} (foe: ${r.attacker.lifeAfter})` : 'you missed';
  const d = r.defender.hit ? `foe hit for ${r.defender.damage} (you: ${r.defender.lifeAfter})` : 'foe missed';
  return `<div class="round"><strong>R${r.index}:</strong> ${a} · ${d}</div>`;
}

type RenownBreakdown = {
  base: number;
  chainBonus: number;
  warBonus: number;
  milestoneBonus: number;
  streakCount: number;
  total: number;
};

/** Itemise the Renown breakdown, e.g. "1 base + 3 chain + 2 war + 25 milestone". */
function renownBreakdownText(r: RenownBreakdown): string {
  const parts = [`${r.base} base`];
  if (r.chainBonus > 0) parts.push(`${r.chainBonus} chain`);
  if (r.warBonus > 0) parts.push(`${r.warBonus} war`);
  if (r.milestoneBonus > 0) parts.push(`${r.milestoneBonus} milestone @ ${r.streakCount}`);
  return parts.join(' + ');
}

/** The floating renown pop-up message shown on an in-House win.
 *  When this win hits a streak milestone, the toast leads with the milestone
 *  callout so it can't be missed. */
function renownToastMessage(r: RenownBreakdown): string {
  if (r.milestoneBonus > 0) {
    return `⚜ Chain milestone! ${r.streakCount} hits — +${r.milestoneBonus} Renown\n+${r.total} total · ${renownBreakdownText(r)}`;
  }
  return `⚜ House Renown +${r.total}\n${renownBreakdownText(r)}`;
}

